import Vue from 'vue'
import {
    computed,
    ref,
} from '@vue/composition-api'

import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { isProductItemValid } from '@/@core/utils/utils'

const { PRODUCT_STATUS, ROLES } = c

// Helper Functions
export function initializeData(root, props) {
    const variantLoaded = ref(false)
    const isLoaded = ref(false)
    const products = ref([])
    const productsSlice = ref([])
    const searchText = ref(null)
    const originalProducts = ref([])
    const rawProducts = ref([])
    const collectionEndDate = ref(null)
    const discountInCredit = ref(0)
    const collectionName = ref('')
    const collectionId = ref(props.cartProducts?.[0]?.collectionId ?? '')
    const isBrand = computed(() => root.$store.getters.entityType === ROLES.BRAND)
    const isCartExport = ref(false)

    return {
        variantLoaded,
        isLoaded,
        products,
        productsSlice,
        searchText,
        originalProducts,
        rawProducts,
        collectionEndDate,
        discountInCredit,
        collectionName,
        collectionId,
        isBrand,
        isCartExport,
    }
}

export function isNotValidMinQty(product, quantity = 0) {
    if (productMinQtyPerStyle(product)) {
        return isNotValidPerStyle(product, quantity)
    } else {
        return isNotValidPerSize(product)
    }
}

export function isNotValidPerStyle(product, quantity) {
    const minQtyPerStyle = productMinQtyPerStyle(product)
    return !!(minQtyPerStyle && quantity < minQtyPerStyle)
}

const hasSizeMinQtyRule = product => !product?.isCarton && productMinQtyPerSize(product) && Array.isArray(product?.items)

export function isNotValidPerSize(product) {
    if (!hasSizeMinQtyRule(product)) return false
    return product.items.some(item => item.value && item.value < productMinQtyPerSize(product))
}

export function minQtyErrorMessage(product) {
    let minQtyText = ''
    if (productMinQtyPerStyle(product)) {
        minQtyText = `style: ${productMinQtyPerStyle(product)}`
    } else if (productMinQtyPerSize(product)) {
        minQtyText = `size: ${productMinQtyPerSize(product)}`
    }
    return minQtyText ? `MOQ per ${minQtyText}` : ''
}

export function calculateSizeTotalValues(items) {
    return items.reduce((total, item) => total + item.value, 0)
}

function collectVariantValues(variants) {
    const values = {}
    variants.forEach(item => {
      for (const { skuId, value } of Object.values(item)) {
        if (skuId) values[skuId] = value
      }
    })
    return values
}

function applyVariantValues(product, currentItems) {
    product.hasError = false
    return product.items.map(item => {
      item.value = currentItems[item.skuId || item.sku] || 0
      if (!product.isCarton && isProductItemValid(item.value, item.quantityAvailable)) {
        product.hasError = true
      }
      return item
    })
}

export const parseProductItems = product => {
    const currentItems = collectVariantValues(product.variants)
    if (product.isCarton || !Object.keys(currentItems).length) return product.items
    return applyVariantValues(product, currentItems)
  }

/**
 * Seeds `product.quantity` to 1 wherever the cart payload omits it.
 *
 * getProductAndOrderTotals already computes with `product.quantity || 1`, so a
 * missing quantity silently reads as one carton in the totals while the Qty
 * input — bound straight to the field — renders blank. The same gap reaches the
 * save payload: buildProductPayloadEntry sends `{ productId, quantity }` for a
 * carton, and an undefined quantity is dropped by JSON.stringify, so the line
 * is persisted with no quantity at all.
 *
 * Mirrors the defaulting the add-products-to-order views already do
 * (V2AddOrderProducts / AddOrderProducts).
 *
 * Vue.set rather than plain assignment: on the fetchCartProducts path the list
 * is `store.getters.getCart.products`, i.e. objects Vuex observed at a time when
 * `quantity` did not exist. A plain assignment adds a non-reactive property
 * there, and `products.value = list` will not re-observe an array that already
 * carries an `__ob__`.
 *
 * Mutates in place and returns nothing — every caller works on the list it
 * passed in.
 */
export const applyProductQuantityDefaults = products => {
    products?.forEach(product => {
      Vue.set(product, 'quantity', product.quantity || 1)
    })
  }

/**
 * Seeds the defaults and takes the cart's before-edit snapshots, in that order.
 *
 * The ordering is load-bearing: buildProductPayloadEntry diffs each product
 * against `originalProducts` to decide whether the user changed it, so seeding
 * after the clone makes every untouched carton look like a pending edit in the
 * retailer save payload. Doing both here means no initialiser can get the order
 * wrong — previously each of them hand-copied the sequence.
 */
export const seedAndSnapshotCartProducts = products => {
    applyProductQuantityDefaults(products)
    return {
      original: JSON.parse(JSON.stringify(products)),
      raw: JSON.parse(JSON.stringify(products)),
    }
  }

  export const getCartProductsErrorStatusText = product => {
    let status = ''
    if (product.status === PRODUCT_STATUS.CANCELLED.VALUE) {
      status = PRODUCT_STATUS.CANCELLED.LABEL
    } else if (product?.hasError) {
      status = 'Out of Stock'
    } else if (!product?.lineTotalQuantity) {
      status = '0 Quantities'
    } else if (product.lineTotalQuantity % 1 !== 0) {
      status = 'Decimal Quantities'
    }
    return status
  }

  const getCartProductsOrderValue = product => {
    if (product.status === PRODUCT_STATUS.CANCELLED.VALUE) {
        return 1
    } else if (product?.hasError) {
        return 2
    } else if (!product?.lineTotalQuantity) {
        return 3
    } else if (product.lineTotalQuantity % 1 !== 0) {
      return 4
    }
    return 5
  }

  export const getSortedCartProducts = products => products
      .sort((a, b) => getCartProductsOrderValue(a) - getCartProductsOrderValue(b))

  export const isProductHasZeroQty = product => !product?.lineTotalQuantity && product.status !== PRODUCT_STATUS.CANCELLED.VALUE

export const isProductWithDecimalQty = product => product.status !== PRODUCT_STATUS.CANCELLED.VALUE && !Number.isInteger(product?.lineTotalQuantity)

function flattenVariantItems(variants) {
  return variants.flatMap(item => Object.keys(item)
    .filter(k => k !== 'color')
    .map(k => ({ sku: item[k].skuId, value: item[k].value || 0 })))
}

function diffVariants(variants, originalItems) {
  const items = []
  let changedCount = 0
  variants.forEach(item => {
    for (const k in item) {
      if (item.hasOwnProperty(k) && k !== 'color') {
        items.push({ sku: item[k].skuId, value: item[k].value || 0 })
        const orig = originalItems.find(i => i.sku === item[k].skuId)
        if (orig.value !== item[k].value) changedCount++
      }
    }
  })
  return { items, changedCount }
}

export function processProductVariants(prod, originalProduct, payload, opts = {}) {
  const { isBrand = { value: false }, forceSave = false } = opts
  if (!originalProduct) {
    payload.products.push({ productId: prod._id, items: flattenVariantItems(prod.variants) })
    return
  }
  const { items, changedCount } = diffVariants(prod.variants, originalProduct.items)
  const shouldInclude = changedCount > 0 || isBrand.value || forceSave
  if (shouldInclude) payload.products.push({ productId: prod._id, items })
}

// KP-4605: a brand can set a maximum order quantity per style inside a
// segmentation. The backend adds `segmentMaxQty` (and `segmentTags`) only to
// styles that carry a rule for this retailer. The product's own per-style and
// per-size minimums still apply; the segment maximum is an extra limit.

export function getSegmentMaxQty(product) {
  const value = Number(product?.segmentMaxQty)
  return Number.isInteger(value) && value > 0 ? value : 0
}

export function hasSegmentMaxQty(product) {
  return getSegmentMaxQty(product) > 0
}

/** The product's own per-style minimum. */
export function productMinQtyPerStyle(product) {
  return product?.minQtyPerStyle || 0
}

/** The product's own per-size minimum. */
export function productMinQtyPerSize(product) {
  return product?.minQtyPerSize || 0
}

export function isAboveSegmentMaxQty(product, units = 0) {
  const segmentMaxQty = getSegmentMaxQty(product)
  return segmentMaxQty > 0 && units > segmentMaxQty
}

/**
 * The fewest units of the style that can be ordered: one carton (or the
 * per-style minimum if higher), otherwise the higher of the per-style and
 * per-size minimums.
 */
export function smallestOrderableUnits(product) {
  const minPerStyle = Number(productMinQtyPerStyle(product)) || 0
  if (product?.isCarton) {
    const unitsPerCarton = (product.items || []).reduce((total, item) => total + (Number(item?.value) || 0), 0)
    return Math.max(minPerStyle, unitsPerCarton)
  }
  return Math.max(minPerStyle, Number(productMinQtyPerSize(product)) || 0)
}

/** KP-4605: a minimum above the segment maximum leaves no quantity that can be ordered. */
export function isSegmentCannotOrder(product) {
  const segmentMaxQty = getSegmentMaxQty(product)
  return segmentMaxQty > 0 && smallestOrderableUnits(product) > segmentMaxQty
}

/** The segment limits block this quantity: the style can't be ordered, or it is above the maximum. */
export function isSegmentQuantityBlocked(product, units = 0) {
  return isSegmentCannotOrder(product) || isAboveSegmentMaxQty(product, units)
}

export function segmentMaxQtyLabel(product) {
  const segmentMaxQty = getSegmentMaxQty(product)
  if (!segmentMaxQty) return ''
  if (isSegmentCannotOrder(product)) {
    return `Can't be ordered: its minimum order quantity (${smallestOrderableUnits(product)}) is above your maximum (${segmentMaxQty})`
  }
  return `Maximum order quantity for this style is ${segmentMaxQty} units`
}

export function segmentMaxQtyMessage(product) {
  const label = segmentMaxQtyLabel(product)
  return label ? `"${product?.name}": ${label}` : ''
}

export function getSegmentTags(product) {
  return Array.isArray(product?.segmentTags) ? product.segmentTags.filter(Boolean) : []
}
